import { findComponentIndex } from './componentMatch';
import { ComponentAlternative, ComponentWithHistory, ValidationResult } from './types';

/**
 * Groups the run's findings and improvement ideas by the BOM component they
 * affect, mirroring Discovery's master–detail. Findings whose affected names
 * match nothing (or that are BOM-wide) live under the "Entire BOM" entry; a
 * finding naming several components appears under each of them and shares one
 * key — resolving it anywhere resolves it everywhere.
 */

export interface FindingRef {
  result: ValidationResult;
  /** Stable per-run identity: rule name + position in the stored results. */
  key: string;
}

export interface ImprovementRef {
  alt: ComponentAlternative;
  /** Index in the run's componentAlternatives array (stable per run). */
  index: number;
  key: string;
  componentIndex: number;
}

export interface ValidationEntry {
  key: string;
  kind: 'bom' | 'component';
  componentIndex: number;
  name: string;
  partNumber?: string;
  mpn?: string;
  quantity?: number;
  description?: string;
  findings: FindingRef[];
  improvements: ImprovementRef[];
}

export const BOM_ENTRY_KEY = 'entire-bom';

export const findingKeyOf = (result: ValidationResult, index: number) =>
  `find-${result.ruleName}-${index}`;

export const improvementKeyOf = (alt: ComponentAlternative, index: number) =>
  `improve-${alt.originalComponentName}-${index}`;

export const buildValidationEntries = (
  ruleResults: ValidationResult[],
  componentAlternatives: ComponentAlternative[],
  components: ComponentWithHistory[],
): ValidationEntry[] => {
  const bomEntry: ValidationEntry = {
    key: BOM_ENTRY_KEY,
    kind: 'bom',
    componentIndex: -1,
    name: 'Entire BOM',
    findings: [],
    improvements: [],
  };
  const componentEntries: ValidationEntry[] = components.map((c, i) => ({
    key: `comp-${i}`,
    kind: 'component',
    componentIndex: i,
    name: c?.name || c?.componentName || `Component ${i + 1}`,
    partNumber: c?.partNumber || undefined,
    mpn: c?.mpn || undefined,
    quantity: typeof c?.quantity === 'number' ? c.quantity : undefined,
    description: c?.description || c?.specifications || undefined,
    findings: [],
    improvements: [],
  }));

  ruleResults.forEach((result, index) => {
    if (result.status === 'pass') return;
    const ref: FindingRef = { result, key: findingKeyOf(result, index) };
    const matched = new Set<number>();
    (result.affectedComponents || []).forEach((name) => {
      const idx = findComponentIndex(components, name);
      if (idx !== -1) matched.add(idx);
    });
    if (matched.size === 0) {
      bomEntry.findings.push(ref);
    } else {
      matched.forEach((idx) => componentEntries[idx].findings.push(ref));
    }
  });

  componentAlternatives.forEach((alt, index) => {
    const componentIndex = findComponentIndex(components, alt.originalComponentName);
    const ref: ImprovementRef = { alt, index, key: improvementKeyOf(alt, index), componentIndex };
    if (componentIndex === -1) {
      bomEntry.improvements.push(ref);
    } else {
      componentEntries[componentIndex].improvements.push(ref);
    }
  });

  return [bomEntry, ...componentEntries];
};

/** Names of components a finding affects beyond the entry it renders under. */
export const otherAffectedNames = (entry: ValidationEntry, ref: FindingRef): string[] => {
  if (entry.kind === 'bom') return [];
  return (ref.result.affectedComponents || []).filter(
    (name) => !entryMatchesName(entry, name),
  );
};

const entryMatchesName = (entry: ValidationEntry, name: string): boolean => {
  const a = normalize(name);
  const b = normalize(entry.name);
  const part = normalize(entry.partNumber || '');
  return !!a && (a === b || a === part || b.includes(a) || a.includes(b));
};

const normalize = (str: string) =>
  str.toLowerCase().replace(/[^a-z0-9]/g, ' ').replace(/\s+/g, ' ').trim();
